import { useCallback, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import {
  uploadGalleryImage,
  deleteGalleryImage,
  GalleryUploadError,
  galleryUploadErrorMessageKey,
} from "@/services/api/eventSettings";
import { cn } from "@/lib/utils";

// Kept in sync with server/app/Http/Controllers/EventGalleryController.php
// (MAX_FILE_SIZE_KB, ALLOWED_EXTENSIONS) — JUB-84. Every accepted format is
// transcoded to JPEG server-side (HEIC in particular can't be rendered by any
// browser), so the whitelist here only gates what's worth uploading at all.
const MAX_SIZE_BYTES = 3 * 1024 * 1024; // 3MB
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
const ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"];
const ACCEPT_ATTR = [...ALLOWED_EXTENSIONS, ...ALLOWED_MIME_TYPES].join(",");

/**
 * iOS Safari and some Android browsers report an empty/generic `file.type` for
 * HEIC camera photos, so the MIME check alone isn't reliable — fall back to
 * the filename extension.
 */
function isAllowedImageFile(file: File): boolean {
  if (ALLOWED_MIME_TYPES.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export interface GalleryUploaderProps {
  eventUuid: string;
  gallery: string[];
  limit: number;
  disabled?: boolean;
  onUpdate: (gallery: string[]) => void;
  onError?: (message: string) => void;
}

export function GalleryUploader({
  eventUuid,
  gallery,
  limit,
  disabled,
  onUpdate,
  onError,
}: GalleryUploaderProps) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setUploading(true);
      // Local running gallery/count — `gallery` prop only updates once onUpdate
      // is called, which would be stale across a multi-file batch (JUB-84).
      let currentGallery = gallery;
      try {
        for (const file of files) {
          if (currentGallery.length >= limit) {
            onError?.(t("events.form.galleryLimitReached", { limit }) ?? `Limit reached (${limit} images)`);
            break;
          }
          if (!isAllowedImageFile(file)) {
            onError?.(t("events.form.galleryInvalidFormat") ?? "Only JPG, PNG, WEBP or HEIC allowed.");
            continue;
          }
          if (file.size > MAX_SIZE_BYTES) {
            onError?.(t("events.form.galleryMaxSize") ?? "Image must not exceed 3MB.");
            continue;
          }
          try {
            const result = await uploadGalleryImage(eventUuid, file);
            currentGallery = result.gallery ?? [...currentGallery, result.url];
            onUpdate(currentGallery);
          } catch (e) {
            const message = e instanceof GalleryUploadError
              ? t(galleryUploadErrorMessageKey(e.code)) ?? e.message
              : e instanceof Error ? e.message : "Upload failed";
            onError?.(message);
          }
        }
      } finally {
        setUploading(false);
      }
    },
    [eventUuid, gallery, limit, onUpdate, onError, t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (disabled) return;
      handleFiles(Array.from(e.dataTransfer.files));
    },
    [disabled, handleFiles],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(Array.from(e.target.files ?? []));
      e.target.value = "";
    },
    [handleFiles],
  );

  const handleDelete = useCallback(
    async (index: number) => {
      setDeletingId(index);
      try {
        await deleteGalleryImage(eventUuid, index);
        const next = gallery.filter((_, i) => i !== index);
        onUpdate(next);
      } catch (e) {
        onError?.(e instanceof Error ? e.message : "Delete failed");
      } finally {
        setDeletingId(null);
      }
    },
    [eventUuid, gallery, onUpdate, onError],
  );

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground" data-testid="gallery-count">
        {gallery.length} {t("events.form.of") ?? "of"} {limit} {t("events.form.images") ?? "images"}
      </p>
      <div
        data-testid="gallery-dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        className={cn(
          "rounded-lg border-2 border-dashed p-6 text-center transition-colors",
          disabled && "pointer-events-none opacity-60",
          !disabled && "hover:border-primary/50",
        )}
      >
        <input
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          onChange={handleChange}
          disabled={disabled || uploading || gallery.length >= limit}
          className="hidden"
          id="gallery-upload-input"
          data-testid="gallery-upload-input"
        />
        <label
          htmlFor="gallery-upload-input"
          className={cn(
            "cursor-pointer text-sm text-muted-foreground",
            (disabled || uploading || gallery.length >= limit) && "cursor-not-allowed opacity-70",
          )}
        >
          {uploading
            ? t("common.loading") ?? "Uploading..."
            : t("events.form.galleryDropzone") ?? "Drop JPG, PNG, WEBP or HEIC here or click to browse (max 3MB)"}
        </label>
      </div>
      {gallery.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {gallery.map((url, index) => (
            <div key={url} className="relative group rounded-lg overflow-hidden border bg-muted" data-testid={`gallery-image-${index}`}>
              <img src={url} alt="" className="w-full aspect-square object-cover" />
              {!disabled && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 opacity-90"
                  disabled={deletingId === index}
                  onClick={() => handleDelete(index)}
                  data-testid={`gallery-delete-${index}`}
                >
                  {deletingId === index ? (t("common.loading") ?? "…") : "×"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
